import { mapMutations, mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { validImageFileName } from '@/utils/vuex';
import inputRules from '@/mixins/input-rules';
import get from 'lodash/get';
import MarkdownItVueLight from 'markdown-it-vue/dist/markdown-it-vue-light.umd.min.js';
// import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/base16-dark.css';
//import 'codemirror/theme/default.css';
import 'codemirror/mode/markdown/markdown.js';
export default {
  name: 'CoreDtwinEditInformation',
  components: {
    // codemirror,
    MarkdownItVueLight
  },
  mixins: [inputRules],
  props: {
    twinObject: {
      type: Object,
      required: true
    },
    account: {
      type: String,
      default: null
    },
    deviceTemplate: {
      type: Object,
      required: false,
      default: null
    },
    twinTemplate: {
      type: Object,
      required: false,
      default: null
    },
    hasTwinManagePermission: {
      type: Boolean,
      required: true
    },
    hasTwinEditPermission: {
      type: Boolean,
      required: true
    },
    hasUserReadPermission: {
      type: Boolean,
      required: true
    },
    hasVisibleParent: {
      type: Boolean,
      required: true
    },
    hideFields: {
      type: Array,
      required: false,
      default: () => []
    },
    tagListType: {
      type: String,
      default: 'digitalTwin',
      required: false
    }
  },
  data: function () {
    return {
      originalTitle: null,
      markdownEdit: false,
      cmOptions: {
        lineWrapping: true,
        tabSize: 4,
        mode: {
          name: 'markdown',
          highlightFormatting: true
        },
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'default',
        line: true,
        lineNumbers: false,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        }
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    computedTwinObject: {
      get() {
        return this.twinObject;
      },
      set(newValue) {
        this.twinObject = {
          ...newValue
        };
      }
    },
    twinCanBeEdited() {
      return this.hasTwinEditPermission && !this.dtwinIsDisabled && !this.cloading;
    },
    dtwinIsDisabled() {
      return get(this.twinObject, 'properties.disabled');
    },
    isCreatedByTemplate() {
      return !!this.deviceTemplate || !!this.twinTemplate;
    },
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    twinTypes() {
      return [{
        value: 'default',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.types.default')
      }, {
        value: 'building',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.types.building')
      }, {
        value: 'sensor',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.types.sensor')
      }, {
        value: 'room',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.types.room')
      }, {
        value: 'door',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.types.door')
      }, {
        value: 'zone',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.types.zone')
      }, {
        value: 'floor',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.types.floor')
      }, {
        value: 'trackable',
        text: this.$i18n_t('DigitalTwin.DigitalTwin.types.trackable')
      }];
    },
    selectedTwinTypeText() {
      const descriptiveType = this.twinTypes.find(t => t.value === this.twinObject.data.type);
      if (descriptiveType) {
        return descriptiveType.text;
      }
      return this.twinObject.data.type;
    },
    cloading() {
      return this.$store.state.app.loading;
    },
    selectedAttachments() {
      if (this.twinObject.data.twinImageId) {
        return [this.twinObject.data.twinImageId];
      }
      return [];
    },
    twinImages() {
      return [{
        name: this.$i18n_t('DigitalTwin.DigitalTwin.states.dataSources.none'),
        id: null
      }].concat((this.twinObject.data && this.twinObject.data.attachments || []).filter(attachment => {
        return validImageFileName(attachment.name);
      }));
    },
    scopeDetailledName() {
      const name = get(this.twinObject, 'scope.name');
      const id = get(this.twinObject, 'scope.id') || get(this.twinObject, 'scopeId');
      if (id !== undefined) {
        return `${name} (ID ${id})`;
      }
      return name;
    }
  },
  watch: {
    twinObject() {
      this.initialMetaDataSetup();
    }
  },
  created() {
    this.initialMetaDataSetup();
  },
  mounted() {},
  methods: {
    ...mapMutations('app', ['snackbar']),
    get,
    handleUpdate(value) {
      this.twinObject['dynamical-data-routing-destination'] = value;
    },
    initialMetaDataSetup() {
      this.originalTitle = get(this.twinObject, 'title');
      if (!this.twinObject.data) {
        this.twinObject.data = {};
      }
    },
    userRelationsUpdated(data) {
      this.twinObject.data.userRelations = data;
    },
    attachmentsUpdated(data) {
      this.twinObject.data.attachments = data;
    },
    twinImageSelected(item) {
      this.twinObject.data.twinImageId = item.id;
    },
    twinImageDeselected() {
      delete this.twinObject.data.twinImageId;
    },
    parentSelectorFilter(item, parentList) {
      return (
        // only allow new parents from the same tenant
        item.tenantId === this.twinObject.tenantId &&
        // obviously cannot select this twin as its own parent
        item.id !== this.twinObject.id &&
        // and it should not be a descendent of this twin
        parentList.findIndex(t => t.id === this.twinObject.id) === -1
      );
    },
    setTags(tags) {
      this.twinObject.data.tags = tags;
    },
    createDigitalTwinScope() {
      ApiService.digitalTwinCreateScope(this, this.twinObject.id).then(() => {
        this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.twinScopeCreationSuccessSnack'), 'success');
        this.$emit('scope-updated');
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.twinScopeCreationFailedSnack'), 'error');
        }
      });
    }
  }
};